import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ViewMode = 'list' | 'folder'
export type BrowseLevel =
  | 'divisions'
  | 'departments'
  | 'calendars'
  | 'events'
  | 'plans'

export interface BrowseDivisionSelection {
  id: string
  name: string
  code: string
}

export interface BrowseDepartmentSelection {
  id: string
  name: string
  code: string
}

export interface BrowseCalendarSelection {
  calendarId: string | null
  calendarName: string
  isSeasonal: boolean
  noCalendar?: boolean
}

export interface BrowseEventSelection {
  eventId: string
  eventName: string
}

export interface BrowsePath {
  division?: BrowseDivisionSelection
  department?: BrowseDepartmentSelection
  calendar?: BrowseCalendarSelection
  event?: BrowseEventSelection
}

interface BrowseState {
  viewMode: ViewMode
  path: BrowsePath

  setViewMode: (mode: ViewMode) => void
  selectDivision: (division: BrowseDivisionSelection) => void
  selectDepartment: (dept: BrowseDepartmentSelection) => void
  selectCalendar: (calendar: BrowseCalendarSelection) => void
  selectEvent: (event: BrowseEventSelection) => void
  navigateToLevel: (level: BrowseLevel) => void
  resetBrowse: () => void
  getCurrentLevel: () => BrowseLevel
}

export const useBrowseStore = create<BrowseState>()(
  persist(
    (set, get) => ({
      viewMode: 'folder',
      path: {},

      setViewMode: mode => set({ viewMode: mode, path: {} }),

      selectDivision: division => set({ path: { division } }),

      selectDepartment: department =>
        set(state => ({
          path: { division: state.path.division, department },
        })),

      selectCalendar: calendar => {
        set(state => {
          // If non-seasonal or no-calendar, skip events level → go straight to plans
          const newPath: BrowsePath = {
            division: state.path.division,
            department: state.path.department,
            calendar,
          }
          return { path: newPath }
        })
      },

      selectEvent: event =>
        set(state => ({
          path: { ...state.path, event },
        })),

      navigateToLevel: level =>
        set(state => {
          const { path } = state
          switch (level) {
            case 'divisions':
              return { path: {} }
            case 'departments':
              return { path: { division: path.division } }
            case 'calendars':
              return {
                path: { division: path.division, department: path.department },
              }
            case 'events':
              return {
                path: {
                  division: path.division,
                  department: path.department,
                  calendar: path.calendar,
                },
              }
            default:
              return {}
          }
        }),

      resetBrowse: () => set({ path: {} }),

      getCurrentLevel: () => {
        const { path } = get()
        if (path.event) return 'plans'
        if (path.calendar) {
          // Non-seasonal or no-calendar → skip events, show plans directly
          if (!path.calendar.isSeasonal || path.calendar.noCalendar)
            return 'plans'
          return 'events'
        }
        if (path.department) return 'calendars'
        if (path.division) return 'departments'
        return 'divisions'
      },
    }),
    {
      name: 'plan-browse-view',
      partialize: state => ({ viewMode: state.viewMode }),
    }
  )
)
